import type {
  CreateEntryRuleDto,
  MakerFlag,
  ManualConfirmedPair
} from '../types/entry.types';
import { FLAG_TO_SIDES, TAB_FLAGS } from '../components/entry/entryMath';
import { INHERIT_SENTINEL, fromSelectValue } from '../components/shared/counterHedgeMode';
import { parseForfaitCapInput } from '../components/shared/forfaitParams';
import { parseWalkInput } from '../components/shared/walkParams';
import type { OpenerDraftField, OpenerDraftValues } from '../drafts/openerDraftFields';

const DEFAULT_TARGET_TNA_PERCENT = 5;
const DEFAULT_THRESHOLD = 0.95;
const DEFAULT_SIZE = 50;
const DEFAULT_MAX_VOL = 500;

/**
 * Everything the three create gestures have to supply. The DRAFT itself, plus
 * the two facts that are not config: which pair, and which gesture.
 */
export interface CreateRuleInput {
  pair: ManualConfirmedPair;
  /**
   * Which of the 4 maker flags this row activated on. Drives the initial SIDES
   * only — the flag is never baked into the create (rules are born paused with
   * every flag off) and the caller applies it by PATCH afterwards so the
   * false→true activation edge fires. `null` when activation came from TT or
   * from the paused-create ✓.
   */
  makerFlag: MakerFlag | null;
  tabPath: 'a' | 'b';
  /** What every control currently shows — `draft.value(...)` for all of them. */
  values: OpenerDraftValues;
  /**
   * Which controls the operator actually INTERACTED with.
   *
   * This — not "differs from the server" — is what decides whether an optional
   * key enters the body. On a rule-less row every baseline is a constant, so a
   * toggle switched OFF compares equal to one never touched; keying off the
   * comparison would drop the key, and `seedCreateDtoFromSubgroup` would then
   * fill it from the subgroup snapshot. The operator's decision would be
   * replaced by someone else's, silently, which is the bug this whole model
   * exists to prevent.
   */
  touched: readonly OpenerDraftField[];
}

/**
 * Fields whose ABSENCE means "inherit". Everything here is gated on `touched`.
 *
 * `threshold`, `size` and `maxVol` are deliberately NOT in this list: for them
 * "not touched" never meant inherit — the row always holds a value (rule →
 * bulk seed → default) and the type declares them required. And omitting
 * `maxVolumeContracts` is not an inherit, it is a **400**: the DTO declares it
 * `@IsNumber() @Min(1)` with no `@IsOptional()`, and the schema `required:
 * true`. Omitting `thresholdSum` is quieter and worse — the rule is born with
 * `null`.
 */
const OMITTABLE: readonly OpenerDraftField[] = [
  'walkSecs',
  'walkCents',
  'cap',
  'lockstep',
  'polyGate',
  'kalshiGate',
  'polyPostOnly',
  'sweep',
  'forfait',
  'allowAboveOne'
];

/**
 * The ONE place a create body is built.
 *
 * It used to be a hand-written whitelist in `EntryPage`, and the row's three
 * gestures each sent something slightly different. Four fields the operator
 * could configure were simply never destructured, so they arrived `undefined`
 * and the subgroup snapshot filled them in — the row showed one value and the
 * rule ran another.
 *
 * Derived from the draft catalog now, and covered by a parity test that fails
 * when a field is added to the catalog without being consumed here.
 */
export function buildCreateRulePayload(input: CreateRuleInput): CreateEntryRuleDto {
  const { pair, makerFlag, tabPath, values, touched } = input;
  const staged = new Set<OpenerDraftField>(touched);
  const was = (field: OpenerDraftField) =>
    OMITTABLE.includes(field) ? staged.has(field) : true;

  const effectiveFlag: MakerFlag = makerFlag ?? TAB_FLAGS[tabPath].poly;
  const { polySide, kalshiSide } = FLAG_TO_SIDES[effectiveFlag];

  // The three required numbers, with the substitutions the three gestures used
  // to each do for themselves.
  //
  // They are here and not at the call sites because that is where they went
  // wrong: the maker switch substituted, the ✓ refused, and the TT switch did a
  // third thing — so what a blank Size meant depended on which control the
  // operator happened to touch. Keeping the substitution is deliberate (arming
  // a leg must not be blocked by a half-typed box) and it is no longer silent:
  // the row DISCARDS its draft after a create, so the box immediately shows
  // what the rule actually got.
  const parsedThr = Number.parseFloat(values.threshold);
  const parsedSize = Number.parseInt(values.size, 10);
  const parsedMaxV = Number.parseFloat(values.maxVol);
  const thresholdSum = Number.isFinite(parsedThr) ? parsedThr : DEFAULT_THRESHOLD;
  const size = Number.isFinite(parsedSize) && parsedSize > 0 ? parsedSize : DEFAULT_SIZE;
  const maxVolumeContracts =
    Number.isFinite(parsedMaxV) && parsedMaxV > 0 ? parsedMaxV : DEFAULT_MAX_VOL;

  const hedge = values.hedgeMode;
  const walkSecs = parseWalkInput(values.walkSecs);
  const walkCents = parseWalkInput(values.walkCents);
  const cap = parseForfaitCapInput(values.cap);

  return {
    pairId: pair._id as string,
    pairName: pair.pairName,
    expiryDate: pair.expiryDate || new Date().toISOString(),
    ruleType: 'sum_of_sides',
    // The row's own path owns its threshold; seeding BOTH paths from one value
    // is what `rules.service` falls back to when only `thresholdSum` arrives.
    thresholdSum,
    ...(tabPath === 'a'
      ? { pathAThresholdSum: thresholdSum }
      : { pathBThresholdSum: thresholdSum }),
    kalshiConfig: pair.kalshi?.ticker
      ? {
          side: kalshiSide,
          ticker: pair.kalshi.ticker,
          contractsToBuy: size,
          targetTnaPercent: DEFAULT_TARGET_TNA_PERCENT
        }
      : undefined,
    polymarketConfig:
      pair.polymarket?.yesTokenId && pair.polymarket?.noTokenId
        ? {
            side: polySide,
            yesTokenId: pair.polymarket.yesTokenId,
            noTokenId: pair.polymarket.noTokenId,
            conditionId: pair.polymarket.conditionId ?? pair.polymarket.yesTokenId,
            contractsToBuy: size,
            targetTnaPercent: DEFAULT_TARGET_TNA_PERCENT
          }
        : undefined,
    maxVolumeContracts,
    volumePeriod: 'day',
    kalshiEnabled: true,
    polymarketEnabled: true,

    // These two are ALWAYS emitted, and it is not an oversight that they are
    // not gated on `touched`.
    //
    // `counterHedgeMode` has a written contract (`counterHedgeMode.ts`): the
    // explicit `null` defends against backends predating the null schema
    // default, where omitting the field let the old non-null default pin the
    // new rule instead of inheriting. Omitting it also hands the key to
    // `seedCreateDtoFromSubgroup` — and a subgroup carrying `market-to-hedge`
    // would give the new rule a counter that crosses the book AT MARKET with no
    // ceiling, while the row shows `Glo`.
    //
    // And the selector HAS an inherit state (`Glo`): the operator can say
    // "inherit" out loud, so there is no need to infer it from silence. The
    // toggles below have no such state — an unchecked box is not a statement —
    // which is exactly why THEY key off interaction.
    makerSizeMode: values.sizeMode,
    counterHedgeMode:
      hedge === INHERIT_SENTINEL ? null : fromSelectValue(hedge),

    // ── everything below is gated on interaction ────────────────────────────
    ...(was('walkSecs') ? { counterMakerWalkStepSeconds: walkSecs ?? null } : {}),
    ...(was('walkCents') ? { counterMakerWalkStepCents: walkCents ?? null } : {}),
    // An out-of-range cap is NOT `null`. `null` is an explicit "no ceiling"
    // that blocks the subgroup seed, so a rule would be born with forfait
    // protection ON and nothing capping the buy — which is precisely what the
    // ✓ refuses to do. Unparseable ⇒ omit and let the seed decide.
    ...(was('cap') && cap !== undefined
      ? { forfaitPolyMaxBuyPrice: cap }
      : {}),
    ...(was('lockstep')
      ? { lockstepMaxAheadBatches: values.lockstep ? 0 : null }
      : {}),
    // The three-state encoding, and the reason this function exists: an
    // explicitly-switched-OFF gate ships `false`, not `null`. `null` is
    // "no opinion", which the inherit chain resolves against the group and the
    // global — so a `null` here would let a group default override the operator.
    ...(was('polyGate')
      ? { disablePlacementGatePolymarket: values.polyGate }
      : {}),
    ...(was('kalshiGate') ? { disablePlacementGateKalshi: values.kalshiGate } : {}),
    ...(was('polyPostOnly') ? { polyMakerPostOnly: values.polyPostOnly } : {}),
    // `sweepDualPostEnabled` is the odd one: its DTO is `boolean` with no
    // `| null` and its schema `default: false`, so "no opinion" can only be
    // expressed by OMITTING the key.
    ...(was('sweep') ? { sweepDualPostEnabled: values.sweep } : {}),
    ...(was('forfait') ? { forfaitProtectionEnabled: values.forfait } : {}),
    ...(was('allowAboveOne') ? { allowThresholdAboveOne: values.allowAboveOne } : {})
  } as CreateEntryRuleDto;
}

/**
 * Which catalog fields this builder actually EMITS, discovered by running it.
 *
 * Derived, not declared. A hand-written list is the same artefact that lost the
 * four fields in the first place — it agrees with the catalog right up until
 * someone adds a column and forgets the spread, and then it agrees with itself
 * instead of with the body.
 *
 * The probe drives each field to a value distinguishable from every other
 * field's, marks them all touched, and reports which ones left a mark on the
 * output.
 */
export function emittedDraftFields(
  probe: (field: OpenerDraftField) => OpenerDraftValues,
  base: OpenerDraftValues,
  pair: ManualConfirmedPair
): OpenerDraftField[] {
  const all = Object.keys(base) as OpenerDraftField[];
  const render = (values: OpenerDraftValues) =>
    JSON.stringify(
      buildCreateRulePayload({
        pair,
        makerFlag: null,
        tabPath: 'a',
        values,
        touched: all
      })
    );
  const baseline = render(base);
  return all.filter(f => render(probe(f)) !== baseline);
}
